import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import IconButton from 'LuxKit/Button/IconButton'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import { useAppSelector } from 'hooks/reduxHooks'
import noop from 'lib/function/noop'
import type { FocusEventHandler } from 'react'
import { useCallback, useRef, useEffect } from 'react'
import * as React from 'react'
import styled from 'styled-components'

const Field = styled.div`
  position: relative;
`

interface Props {
  className?: string
  onChange: (text: string) => void
  onClear?: () => void
  required?: boolean
  requiredErrorMessage?: string
  placeholder?: string
  value?: string
  searchPhrase?: string
  autoFocus?: boolean
  autoSelect?: boolean
  onFocus?: React.FocusEventHandler<HTMLInputElement>
}

function SearchFormModalTextField(props: Props) {
  const {
    value = '',
    placeholder,
    className,
    onChange,
    onClear = noop,
    required,
    requiredErrorMessage,
    searchPhrase,
    autoFocus = false,
    autoSelect = false,
    onFocus,
  } = props
  const headlessMode = useAppSelector(state => state.system.headlessMode)
  const localInputRef = useRef<HTMLInputElement>(null)

  const handleFocus = useCallback<FocusEventHandler<HTMLInputElement>>((event) => {
    if (event.currentTarget.value && autoSelect && !headlessMode) {
      event.currentTarget.select()
    }
    onFocus?.(event)
  }, [onFocus, autoSelect, headlessMode])

  useEffect(() => {
    if (autoFocus && !headlessMode) {
      localInputRef.current?.focus({ preventScroll: true })
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const canClear = !!(searchPhrase || value)
  const onExtraActionClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(() => {
    if (localInputRef.current) {
      if (canClear) {
        localInputRef.current.value = ''
        localInputRef.current.dispatchEvent(new Event('change', { bubbles: true }))
        onClear()
      } else {
        localInputRef.current.focus()
      }
    }
  }, [canClear, onClear])

  return <Field className={className}>
    {!!required && <HiddenInput
      value={value ? 'filled' : undefined}
      required
      invalidMessage={requiredErrorMessage}
    />}
    <TextInputOnly
      noValidationSpacing
      placeholder={placeholder}
      ref={localInputRef}
      value={searchPhrase ?? value}
      onChange={e => onChange(e.currentTarget.value)}
      onFocus={handleFocus}
      endIcon={<IconButton kind="tertiary" onClick={onExtraActionClick}>
        {canClear && <LineTimesIcon />}
        {!canClear && <LineSearchIcon />}
      </IconButton>}
    />
  </Field>
}

export default SearchFormModalTextField
